import { useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  extractErrorMessage,
  useGetRrcLocationProfilesByLocationQuery,
  useGetCurrentRrcLocationProfilesByLocationQuery,
} from '@ots-frontend/ots-data';
import { AdminPage, type AdminPageProps } from '../../components/AdminPage';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { OtsLocationSelect } from '../../select/OtsLocationSelect';
import { OtsThemeRoot } from '../../theme/OtsTheme';
import { useApp, useAppDensity } from '../../app/AppContext';
import { useAuth } from '../../app/AuthProvider';
import { RrcLocationProfilesView } from './RrcLocationProfilesView';
import { RrcLocationProfileInspector } from './RrcLocationProfileInspector';

export type RrcLocationProfilesPageProps = {
  locationId?: string;
  locationType?: string;
  locationName?: string;
  compact?: boolean;
  embedded?: boolean;
  readOnly?: boolean;
  layout?: AdminPageProps['layout'];
};
const LocationProfiles = ({
  locationId: fixedId,
  locationType: fixedType,
  locationName: fixedName,
  compact: compactProp,
  embedded,
  layout = 'document',
  readOnly = false,
}: RrcLocationProfilesPageProps) => {
  const auth = useAuth(),
    app = useApp(),
    density = useAppDensity();
  const compact = compactProp ?? density === 'compact';
  const fill = layout === 'fill';
  const [location, setLocation] = useState({
    id: fixedId,
    type: fixedType ?? 'SITE',
    name: fixedName,
  });
  const [includeHistory, setIncludeHistory] = useState(false);
  const [selectedId, select] = useState<string>();
  const [creating, setCreating] = useState(false);
  const canView = auth.hasAccess('rrclocationprofile_view');
  const canCreate = !readOnly && auth.hasAccess('rrclocationprofile_create');
  const validType = ['SITE', 'WELL'].includes(location.type);
  const currentQuery = useGetCurrentRrcLocationProfilesByLocationQuery(
    canView && location.id && validType && !includeHistory
      ? { locationId: location.id, locationType: location.type }
      : skipToken
  );
  const historyQuery = useGetRrcLocationProfilesByLocationQuery(
    canView && location.id && validType && includeHistory
      ? { locationId: location.id, locationType: location.type }
      : skipToken
  );
  const query = includeHistory ? historyQuery : currentQuery;
  const content = !canView ? (
    <OtsInlineAlert title="Access denied" tone="warning">
      You do not have permission to view location profiles.
    </OtsInlineAlert>
  ) : (
    <>
      {!fixedId && (
        <div className={fill ? 'shrink-0 max-w-2xl' : 'mb-4 max-w-2xl'}>
          <OtsLocationSelect
            locationType={location.type}
            value={location.id}
            locationName={location.name}
            regionId={app?.regionId}
            size={compact ? 'compact' : 'default'}
            onChange={(id, type) => {
              setLocation({ id, type, name: undefined });
              select(undefined);
              setCreating(false);
            }}
            onNameChange={(name) =>
              setLocation((value) => ({ ...value, name }))
            }
          />
        </div>
      )}
      {!location.id || !validType ? (
        <OtsInlineAlert title="Select a location">
          Choose a site or well to manage its RRC registrations and profile
          history.
        </OtsInlineAlert>
      ) : (
        <RrcLocationProfilesView
          fill={fill}
          profiles={(query.currentData ?? []).filter((profile) =>
            auth.hasAccess(profile, 'rrclocationprofile_view')
          )}
          loading={query.isFetching}
          error={
            query.error
              ? extractErrorMessage(
                  query.error,
                  'Location profiles could not be loaded.'
                )
              : undefined
          }
          compact={compact}
          includeHistory={includeHistory}
          onIncludeHistory={setIncludeHistory}
          onRefresh={() => void query.refetch()}
          onOpen={(profile) => select(profile.id)}
          onNew={canCreate ? () => setCreating(true) : undefined}
        />
      )}
      {selectedId && (
        <RrcLocationProfileInspector
          id={selectedId}
          compact={compact}
          readOnly={readOnly}
          onClose={() => select(undefined)}
          onChanged={() => void query.refetch()}
        />
      )}
      {creating && !readOnly && (
        <RrcLocationProfileInspector
          initialMode="create"
          locationLocked
          compact={compact}
          defaults={{
            locationId: location.id,
            locationType: location.type,
            locationName: location.name,
          }}
          onClose={() => setCreating(false)}
          onChanged={() => void query.refetch()}
        />
      )}
    </>
  );
  const body = (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col gap-4' : 'contents'}>
      {content}
    </div>
  );
  return (
    <OtsThemeRoot
      className={fill ? 'flex h-full min-h-0 flex-1 flex-col' : undefined}
    >
      {embedded ? (
        body
      ) : (
        <AdminPage
          title="Location compliance profiles"
          eyebrow="Texas RRC"
          fullWidth
          layout={layout}
          density={compact ? 'compact' : 'default'}
          subtitle="Site and well registrations, certification settings, and effective version history."
        >
          <div
            className={
              fill ? 'mt-4 flex min-h-0 flex-1 flex-col overflow-auto' : 'mt-5'
            }
          >
            {body}
          </div>
        </AdminPage>
      )}
    </OtsThemeRoot>
  );
};
export const RrcLocationProfilesPage = (
  props: RrcLocationProfilesPageProps
) => (
  <LocationProfiles
    key={JSON.stringify([props.locationType, props.locationId])}
    {...props}
  />
);
